import * as Sentry from "@sentry/gatsby";

import { assets } from "./constants";
import { SCAN_STATUS } from "./types";

const sounds = {
  success: assets.ding,
  rejected: assets.scanRejected,
  error: assets.scanError,
};
type Sound = keyof typeof sounds;
let context: AudioContext | undefined;
let resuming: Promise<void> | undefined;
let activeSource: AudioBufferSourceNode | undefined;
let playback = 0;
const buffers: Partial<Record<Sound, Promise<AudioBuffer>>> = {};

const getContext = () => {
  if (!context && typeof window !== "undefined") {
    const AudioContextClass =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioContextClass) context = new AudioContextClass();
  }
  return context;
};

const load = (
  sound: Sound,
  audioContext: AudioContext
): Promise<AudioBuffer> => {
  if (!buffers[sound]) {
    buffers[sound] = fetch(sounds[sound])
      .then((response) => {
        if (!response.ok)
          throw new Error(`Could not load scan sound: ${sound}`);
        return response.arrayBuffer();
      })
      .then((data) => audioContext.decodeAudioData(data))
      .catch((error) => {
        delete buffers[sound];
        throw error;
      });
  }
  return buffers[sound]!;
};

const stop = () => {
  playback++;
  const source = activeSource;
  activeSource = undefined;
  try {
    source?.stop();
    source?.disconnect();
  } catch (error) {
    Sentry.captureException(error);
  }
};

const unlock = () => {
  try {
    const audioContext = getContext();
    if (!audioContext) return;
    if (audioContext.state !== "running" && !resuming) {
      resuming = audioContext
        .resume()
        .catch((error) => {
          Sentry.captureException(error);
        })
        .finally(() => {
          resuming = undefined;
        });
    }
    (Object.keys(sounds) as Sound[]).forEach((sound) => {
      void load(sound, audioContext).catch((error) =>
        Sentry.captureException(error)
      );
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

const playSound = (sound: Sound) => {
  try {
    stop();
    const currentPlayback = playback;
    const audioContext = getContext();
    // Never queue a stale result to play when a later tap unlocks the context.
    if (!audioContext || (audioContext.state !== "running" && !resuming))
      return;
    void Promise.all([load(sound, audioContext), resuming])
      .then(([buffer]) => {
        if (currentPlayback !== playback || audioContext.state !== "running")
          return;
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.onended = () => {
          source.disconnect();
          if (activeSource === source) activeSource = undefined;
        };
        activeSource = source;
        source.start();
      })
      .catch((error) => Sentry.captureException(error));
  } catch (error) {
    Sentry.captureException(error);
  }
};

const scanResult = (status: SCAN_STATUS) => {
  if (status === "success") playSound("success");
  else if (status !== "idle") playSound("rejected");
};

export default {
  unlock,
  stop,
  scanResult,
  scanError: () => playSound("error"),
  scanBarcode: () => playSound("success"),
};
